export type MobileTitleNamespace =
  | 'common'
  | 'dashboard'
  | 'invoices'
  | 'invoiceImport'
  | 'quotes'
  | 'recurringInvoices'
  | 'clients'
  | 'templates'
  | 'products'
  | 'productLists'
  | 'supplierBills'
  | 'financialStatements'
  | 'ledger'
  | 'annualAccounts'
  | 'fixedAssets'
  | 'vat'
  | 'employees'
  | 'payslips'
  | 'absences'
  | 'company'
  | 'fiduciaire'
  | 'account'
  | 'support'
  | 'admin'
  | 'adminStats';

export type MobileTitleKey = `${MobileTitleNamespace}:${string}`;

export interface MobileTitleContext {
  isSupport: boolean;
}

export interface MobileTitleMatch {
  handle?: unknown;
  params?: Readonly<Record<string, string | undefined>>;
}

type MobileTitleResolver = (args: {
  context: MobileTitleContext;
  params: Readonly<Record<string, string | undefined>>;
}) => MobileTitleKey;

export interface MobileTitleHandle {
  mobileTitle: MobileTitleKey | MobileTitleResolver;
}

function mobileTitle(
  title: MobileTitleHandle['mobileTitle'],
): MobileTitleHandle {
  return { mobileTitle: title };
}

const recordOrCreateTitle =
  (
    recordTitle: MobileTitleKey,
    createTitle: MobileTitleKey,
  ): MobileTitleResolver =>
  ({ params }) =>
    params.slug === 'new' ? createTitle : recordTitle;

export const MOBILE_TITLE_HANDLES = {
  dashboard: mobileTitle('dashboard:title'),
  quotes: mobileTitle('quotes:quotes.title'),
  quoteEdit: mobileTitle('quotes:mobileTitle.edit'),
  bills: mobileTitle('invoices:bills.title'),
  billEdit: mobileTitle('invoices:mobileTitle.edit'),
  recurringInvoices: mobileTitle('recurringInvoices:title'),
  recurringInvoiceCreate: mobileTitle('recurringInvoices:actions.new'),
  recurringInvoiceEdit: mobileTitle('recurringInvoices:mobileTitle.edit'),
  clientImport: mobileTitle('clients:import.title'),
  billImport: mobileTitle('invoiceImport:page.title'),
  templates: mobileTitle('templates:title'),
  clients: mobileTitle(
    recordOrCreateTitle('clients:title', 'clients:actions.new'),
  ),
  products: mobileTitle(
    recordOrCreateTitle('products:title', 'products:actions.new'),
  ),
  productLists: mobileTitle(
    recordOrCreateTitle('productLists:title', 'productLists:actions.new'),
  ),
  expenses: mobileTitle('supplierBills:page.title'),
  expenseReview: mobileTitle('supplierBills:review.title'),
  expenseImport: mobileTitle('supplierBills:import.pageTitle'),
  financialStatements: mobileTitle('financialStatements:page.title'),
  financialStatementImport: mobileTitle('financialStatements:import.title'),
  financialStatementReconcile: mobileTitle(
    'financialStatements:reconciliation.title',
  ),
  annualAccounts: mobileTitle('annualAccounts:page.title'),
  chartOfAccounts: mobileTitle('ledger:chart.title'),
  ledgerActivation: mobileTitle('ledger:activation.wizardTitle'),
  journal: mobileTitle('ledger:journal.title'),
  manualEntry: mobileTitle('ledger:manual.title'),
  vatReturns: mobileTitle('vat:title'),
  fiscalYears: mobileTitle('ledger:fiscalYears.title'),
  fixedAssets: mobileTitle('fixedAssets:title'),
  closing: mobileTitle('ledger:closing.mobileTitle'),
  employees: mobileTitle(
    recordOrCreateTitle('employees:title', 'employees:actions.new'),
  ),
  employeePayslips: mobileTitle('payslips:title'),
  employeeAbsences: mobileTitle('absences:title'),
  employeeChangeRequests: mobileTitle('employees:requests.title'),
  payslips: mobileTitle('payslips:title'),
  company: mobileTitle('company:title'),
  mandates: mobileTitle('fiduciaire:mandatesPage.title'),
  mandateRequest: mobileTitle('fiduciaire:respond.title'),
  account: mobileTitle('account:title'),
  support: mobileTitle(({ context }) =>
    context.isSupport ? 'support:supportTitle' : 'support:title',
  ),
  adminData: mobileTitle('admin:title'),
  adminStats: mobileTitle('adminStats:title'),
  adminCompliance: mobileTitle('admin:compliance.title'),
  notFound: mobileTitle('common:notFound.title'),
} satisfies Record<string, MobileTitleHandle>;

function isMobileTitleHandle(handle: unknown): handle is MobileTitleHandle {
  return (
    typeof handle === 'object' && handle !== null && 'mobileTitle' in handle
  );
}

export function resolveMobileTitle(
  matches: readonly MobileTitleMatch[],
  context: MobileTitleContext,
): MobileTitleKey {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    if (!match || !isMobileTitleHandle(match.handle)) continue;

    const { mobileTitle: title } = match.handle;
    return typeof title === 'function'
      ? title({ context, params: match.params ?? {} })
      : title;
  }

  return 'common:notFound.title';
}
